import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './styles/LoginPage.css';
import { API_BASE_URL } from '../utils/api';
import { trackEvent, trackException } from '../services/applicationInsights';
import { useAuth } from '../auth/AuthProvider';
import { clearStoredOAuthTokens } from '../auth/tokenStorage';
import logoImage from '../assets/images/logo.jpeg';

// Main Auth Container - Handles routing between Login, Signup, and Reset Password
export default function AuthPage() {
  const [currentPage, setCurrentPage] = useState('login'); // 'login', 'signup', 'reset'
  const navigate = useNavigate();
  const location = useLocation();

  const setAuthPage = (page) => {
    if (page !== 'login' && page !== 'signup' && page !== 'reset') return;
    setCurrentPage(page);
    navigate(page === 'login' ? '/' : `/?page=${page}`, { replace: true });
  };

  useEffect(() => {
    const pageFromUrl = new URLSearchParams(location.search).get('page');
    if (pageFromUrl === 'login' || pageFromUrl === 'signup' || pageFromUrl === 'reset') {
      setCurrentPage(pageFromUrl);
    }
  }, [location.search]);

  const oauthQueryError = (() => {
    const params = new URLSearchParams(location.search);
    const oauthError = params.get('error');
    const oauthErrorDescription = params.get('error_description');
    if (!oauthError) return '';
    return friendlyMsg(oauthErrorDescription || `OAuth sign-in failed (${oauthError}).`);
  })();

  return (
    <div className="auth-page">
      {/* Background */}
      <div className="auth-background">
        <div className="auth-bg-blur"></div>
      </div>

      {/* Navigation Header */}
      <header className="auth-header">
        <div className="auth-header-brand">
          <img src={logoImage} alt="Cognior " className="auth-header-logo" />
          <span className="auth-header-text">Cognior </span>
        </div>
      </header>

      {/* Main Container */}
      <main className="auth-main">
        {currentPage === 'login' && <LoginForm setPage={setAuthPage} navigate={navigate} initialError={oauthQueryError} />}
        {currentPage === 'signup' && <SignupForm setPage={setAuthPage} navigate={navigate} />}
        {currentPage === 'reset' && <ResetPasswordForm setPage={setAuthPage} navigate={navigate} />}
      </main>
    </div>
  );
}

// ==================== HELPERS ====================
// Safe JSON parser — handles plain-text responses without crashing
async function safeJson(response) {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { message: text }; // Wrap plain text so .message always works
  }
}

// Extract accurate, readable error message from API response data (handles arrays, dictionaries, or string messages)
function extractErrorMessage(data, fallbackMessage = 'An unexpected error occurred. Please try again.') {
  if (!data) return fallbackMessage;

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const msgs = data.errors
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        return entry?.message || entry?.errorMessage || '';
      })
      .filter(Boolean);
    if (msgs.length > 0) return msgs.join(' ');
  }

  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    const msgs = Object.values(data.errors).flat().filter(Boolean);
    if (msgs.length > 0) return msgs.join(' ');
  }

  if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
  if (typeof data.error_description === 'string' && data.error_description.trim()) return data.error_description.trim();
  if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof data.detail === 'string' && data.detail.trim()) return data.detail.trim();
  if (typeof data.title === 'string' && data.title.trim()) return data.title.trim();
  if (typeof data === 'string' && data.trim()) return data.trim();

  return fallbackMessage;
}

// Map technical errors to user-friendly messages — never show raw JSON/HTML/code
function friendlyMsg(msg) {
  if (!msg) return 'Something went wrong. Please try again.';

  // If it contains HTML, JSON, code patterns, or is too long → generic message
  if (msg.length > 200
    || /<[a-z/]|<!doctype/i.test(msg)
    || /[{}[\]]/.test(msg)
    || /Unexpected token/.test(msg)
    || /SyntaxError|TypeError|ReferenceError/.test(msg)
    || /stack trace|at .+\(/.test(msg)
    || /Failed to fetch|NetworkError/i.test(msg)
  ) {
    return 'Unable to connect to the server. Please try again later.';
  }

  return msg;
}

// ==================== LOGIN FORM ====================
function LoginForm({ setPage, navigate, initialError = '' }) {
  const { login } = useAuth(); // Use the hook
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Clear existing auth on mount to prevent stale state issues
  useEffect(() => {
    clearStoredOAuthTokens();
    localStorage.removeItem('email');
  }, []);

  useEffect(() => {
    if (initialError) {
      setError(initialError);
    }
  }, [initialError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    trackEvent('Login Attempt', { email });

    try {
      await login(email, password);
      // Login successful - AuthProvider updates state, but we might want to track success
      trackEvent('Login Success', { email });
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      const msg = friendlyMsg(err.message);
      setError(msg);
      trackEvent('Login Failed', { email, error: msg });
      trackException(err, { action: 'login', email });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container login-container">
      <div className="auth-box">
        <div className="auth-box-header">
          <h1 className="auth-title">Welcome Back</h1>
          <p className="auth-subtitle">Sign in to your Cognior  account</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-input-group">
            <label htmlFor="login-email">Email Address</label>
            <div className="auth-input-wrapper">
              <input
                id="login-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <span className="auth-input-icon">✉</span>
            </div>
          </div>

          <div className="auth-input-group">
            <label htmlFor="login-password">Password</label>
            <div className="auth-input-wrapper">
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {error && <div className="auth-error" role="alert">{error}</div>}

          <button type="submit" className="auth-btn-primary" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </form>

        <div className="auth-divider">or</div>

        <div className="auth-links">
          <button
            type="button"
            className="auth-link-btn"
            onClick={() => setPage('signup')}
          >
            Don't have an account? <strong>Sign Up</strong>
          </button>
          <button
            type="button"
            className="auth-link-btn"
            onClick={() => setPage('reset')}
          >
            Forgot password? <strong>Reset it</strong>
          </button>
        </div>
      </div>

      <div className="auth-hero">
        <div className="auth-hero-content">
          <h2>Cognior Intelligent Adoption Platform</h2>
          <p>Build better digital adoption experience with our leading IAP solution</p>
        </div>
      </div>
    </div>
  );
}

// ==================== SIGNUP FORM ====================
function SignupForm({ setPage, navigate }) {
  const [formData, setFormData] = useState({
    employeeId: '',
    organizationId: '',
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*]/.test(password)) strength++;
    setPasswordStrength(strength);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'password') checkPasswordStrength(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    trackEvent('Registration Attempt', { email: formData.email });

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await safeJson(response);

      if (response.ok && data.id) {
        trackEvent('Registration Success', { email: formData.email });
        alert('✅ Account created successfully!\nAwaiting admin approval to access.');
        setPage('login');
        return;
      }

      throw new Error(extractErrorMessage(data, 'Registration failed. Please try again.'));
    } catch (err) {
      setError(friendlyMsg(err.message));
      trackEvent('Registration Failed', { email: formData.email, error: err.message });
      trackException(err, { action: 'register' });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = (strength) => {
    const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#10b981'];
    return colors[strength] || '#ef4444';
  };

  const getPasswordStrengthLabel = (strength) => {
    const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong', 'Excellent'];
    return labels[strength] || 'Weak';
  };

  return (
    <div className="auth-container signup-container">
      <div className="auth-box signup-box">
        <div className="auth-box-header">
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">Join Cognior Digital Adoption platform</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-grid">
            <div className="auth-input-group">
              <label htmlFor="employeeId">Employee ID</label>
              <input
                id="employeeId"
                type="text"
                name="employeeId"
                placeholder="EMP-12345"
                value={formData.employeeId}
                onChange={handleChange}
                required
              />
            </div>
            <div className="auth-input-group">
              <label htmlFor="organizationId">Organization ID</label>
              <input
                id="organizationId"
                type="text"
                name="organizationId"
                placeholder="001"
                value={formData.organizationId}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="auth-form-grid">
            <div className="auth-input-group">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                name="firstName"
                placeholder="Alex"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="auth-input-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                name="lastName"
                placeholder="Smith"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="auth-input-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              name="username"
              placeholder="AlexSmith"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-input-group">
            <label htmlFor="signup-email">Email Address</label>
            <input
              id="signup-email"
              type="email"
              name="email"
              placeholder="you@company.com"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="auth-input-group">
            <label htmlFor="signup-password">Password</label>
            <div className="auth-input-wrapper">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className="auth-input-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {formData.password && (
              <div className="auth-password-strength">
                <div className="auth-strength-bars">
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      className={`auth-strength-bar ${i < passwordStrength ? 'filled' : ''}`}
                      style={{
                        backgroundColor: i < passwordStrength ? getPasswordStrengthColor(passwordStrength - 1) : 'transparent'
                      }}
                      role="progressbar"
                      aria-valuenow={i < passwordStrength ? 100 : 0}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    />
                  ))}
                </div>
                <span style={{ color: getPasswordStrengthColor(passwordStrength - 1) }}>
                  {getPasswordStrengthLabel(passwordStrength)}
                </span>
              </div>
            )}
            <div className="auth-hint">Min 8 chars, 1 upper, 1 number, 1 special (!@#$%^&*)</div>
          </div>

          <div className="auth-input-group">
            <label htmlFor="signup-confirm-password">Confirm Password</label>
            <input
              id="signup-confirm-password"
              type="password"
              name="confirmPassword"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
          </div>

          {error && <div className="auth-error" role="alert">{error}</div>}

          <button type="submit" className="auth-btn-primary" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </form>

        <p className="auth-agreement">
          After registration, you'll need admin approval to access your account.
        </p>

        <button
          type="button"
          className="auth-link-btn"
          onClick={() => setPage('login')}
        >
          Already have an account? <strong>Sign In</strong>
        </button>
      </div>
    </div>
  );
}

// ==================== COUNTDOWN TIMER COMPONENT ====================
function CountdownTimer({ seconds, onExpire, label = "Time remaining" }) {
  const [timeLeft, setTimeLeft] = useState(seconds);

  useEffect(() => {
    setTimeLeft(seconds);
  }, [seconds]);

  useEffect(() => {
    if (timeLeft <= 0) {
      onExpire();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, onExpire]);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    const percentage = (timeLeft / seconds) * 100;
    if (percentage > 50) return '#22c55e';
    if (percentage > 25) return '#eab308';
    return '#ef4444';
  };

  return (
    <div className="auth-timer" role="timer" aria-live="polite">
      <div className="auth-timer-icon">⏱️</div>
      <div className="auth-timer-content">
        <span className="auth-timer-label">{label}</span>
        <span className="auth-timer-value" style={{ color: getTimerColor() }}>
          {formatTime(timeLeft)}
        </span>
      </div>
      <div className="auth-timer-progress">
        <div
          className="auth-timer-progress-bar"
          style={{
            width: `${(timeLeft / seconds) * 100}%`,
            backgroundColor: getTimerColor()
          }}
        />
      </div>
    </div>
  );
}

// ==================== RESET PASSWORD FORM ====================
function ResetPasswordForm({ setPage, navigate }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [orgId, setOrgId] = useState('');
  const [resetId, setResetId] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  // Timer states
  const [step2ExpiryTime, setStep2ExpiryTime] = useState(0);
  const [step3ExpiryTime, setStep3ExpiryTime] = useState(0);

  const checkPasswordStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[!@#$%^&*]/.test(pwd)) strength++;
    setPasswordStrength(strength);
  };

  const handleTimerExpire = (stepNumber) => {
    setError(`⏰ Verification code expired. Please request a new one.`);
    if (stepNumber === 2) {
      setTimeout(() => {
        setStep(1);
        setResetId('');
        setCode('');
        setError('');
      }, 2000);
    } else if (stepNumber === 3) {
      setTimeout(() => {
        setStep(1);
        setResetId('');
        setResetToken('');
        setCode('');
        setPassword('');
        setConfirmPassword('');
        setError('');
      }, 2000);
    }
  };

  // Step 1: Request password reset and get resetId
  const handleStep1 = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/password-reset/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          organizationId: orgId
        }),
      });

      const data = await safeJson(response);

      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to send verification code. Please try again.'));
      }

      if (data.resetId) {
        setResetId(data.resetId);
        setStep2ExpiryTime(data.expiresInSeconds || 600);
        setSuccess('✓ Verification code sent to your email!');
        trackEvent('Password Reset Code Sent', { email });

        setTimeout(() => {
          setStep(2);
          setSuccess('');
        }, 1500);
      } else {
        throw new Error('Unable to start password reset. Please try again.');
      }
    } catch (err) {
      setError(friendlyMsg(err.message));
      trackEvent('Password Reset Code Failed', { email, error: err.message });
      trackException(err, { action: 'reset_password_step1', email });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code and get resetToken
  const handleStep2 = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/password-reset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resetId: resetId,
          code: code,
          organizationId: orgId
        }),
      });

      const data = await safeJson(response);

      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Invalid verification code. Please try again.'));
      }

      if (data.resetToken) {
        setResetToken(data.resetToken);
        setStep3ExpiryTime(data.expiresInSeconds || 300);
        setSuccess('✓ Code verified!');
        trackEvent('Password Reset Code Verified', { email });

        setTimeout(() => {
          setStep(3);
          setSuccess('');
        }, 1500);
      } else {
        throw new Error('Verification failed. Please request a new code.');
      }
    } catch (err) {
      setError(friendlyMsg(err.message));
      trackEvent('Password Reset Code Verification Failed', { email, error: err.message });
      trackException(err, { action: 'reset_password_step2', email });
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Complete password reset using resetToken
  const handleStep3 = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordStrength < 2) {
      setError('Password is too weak');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/password-reset/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resetToken: resetToken,
          newPassword: password,
          organizationId: orgId
        }),
      });

      const data = await safeJson(response);

      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Failed to reset password. Please try again.'));
      }

      setSuccess('✓ Password reset successfully! Redirecting to login...');
      trackEvent('Password Reset Success', { email });

      setTimeout(() => setPage('login'), 2000);
    } catch (err) {
      setError(friendlyMsg(err.message));
      trackEvent('Password Reset Failed', { email, error: err.message });
      trackException(err, { action: 'reset_password_step3', email });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = (strength) => {
    const colors = ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#10b981'];
    return colors[strength] || '#ef4444';
  };

  const handleResendCode = () => {
    setStep(1);
    setResetId('');
    setCode('');
    setResetToken('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="auth-container reset-container">
      <div className="auth-box reset-box">
        <div className="auth-box-header">
          <h1 className="auth-title">Reset Password</h1>
          <p className="auth-subtitle">Step {step} of 3</p>
        </div>

        <div className="auth-step-indicator" role="progressbar" aria-valuenow={step} aria-valuemin="1" aria-valuemax="3">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`auth-step-dot ${s <= step ? 'active' : ''}`}
            />
          ))}
        </div>

        {/* Step 1: Email & Org */}
        {step === 1 && (
          <form onSubmit={handleStep1} className="auth-form">
            <div className="auth-input-group">
              <label htmlFor="reset-email">Email Address</label>
              <input
                id="reset-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="auth-input-group">
              <label htmlFor="reset-orgId">Organization ID</label>
              <input
                id="reset-orgId"
                type="text"
                placeholder="ORG-001"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                required
              />
            </div>

            {error && <div className="auth-error" role="alert">{error}</div>}
            {success && <div className="auth-success" role="status">{success}</div>}

            <button type="submit" className="auth-btn-primary" disabled={loading}>
              {loading ? 'Sending...' : 'Send Verification Code'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </form>
        )}

        {/* Step 2: Verify Code */}
        {step === 2 && (
          <form onSubmit={handleStep2} className="auth-form">
            <div className="auth-info-box">
              Verification code sent to <strong>{email}</strong>. Check your inbox and spam folder.
            </div>

            {/* Timer for Step 2 */}
            {step2ExpiryTime > 0 && (
              <CountdownTimer
                seconds={step2ExpiryTime}
                onExpire={() => handleTimerExpire(2)}
                label="Code expires in"
              />
            )}

            <div className="auth-input-group">
              <label htmlFor="code-input">Enter 6-Digit Code</label>
              <input
                id="code-input"
                type="text"
                placeholder="000000"
                maxLength="6"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </div>

            {error && <div className="auth-error" role="alert">{error}</div>}
            {success && <div className="auth-success" role="status">{success}</div>}

            <button type="submit" className="auth-btn-primary" disabled={loading || code.length !== 6}>
              {loading ? 'Verifying...' : 'Verify Code'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>

            <div className="auth-form-grid">
              <button
                type="button"
                className="auth-btn-secondary"
                onClick={() => {
                  setStep(1);
                  setCode('');
                  setResetId('');
                  setError('');
                }}
              >
                ← Back
              </button>
              <button
                type="button"
                className="auth-btn-secondary"
                onClick={handleResendCode}
              >
                Resend Code
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Set New Password */}
        {step === 3 && (
          <form onSubmit={handleStep3} className="auth-form">
            {/* Timer for Step 3 */}
            {step3ExpiryTime > 0 && (
              <CountdownTimer
                seconds={step3ExpiryTime}
                onExpire={() => handleTimerExpire(3)}
                label="Session expires in"
              />
            )}

            <div className="auth-input-group">
              <label htmlFor="reset-password">New Password</label>
              <div className="auth-input-wrapper">
                <input
                  id="reset-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    checkPasswordStrength(e.target.value);
                  }}
                  required
                />
                <button
                  type="button"
                  className="auth-input-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {password && (
                <div className="auth-password-strength">
                  <div className="auth-strength-bars">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className={`auth-strength-bar ${i < passwordStrength ? 'filled' : ''}`}
                        style={{
                          backgroundColor: i < passwordStrength ? getPasswordStrengthColor(passwordStrength - 1) : 'transparent'
                        }}
                        role="progressbar"
                        aria-valuenow={i < passwordStrength ? 100 : 0}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    ))}
                  </div>
                  <span style={{ color: getPasswordStrengthColor(passwordStrength - 1) }}>
                    {['Weak', 'Fair', 'Good', 'Strong', 'V Strong', 'Excellent'][passwordStrength] || 'Weak'}
                  </span>
                </div>
              )}
              <div className="auth-hint">Min 8 chars, 1 upper, 1 number, 1 special (!@#$%^&*)</div>
            </div>

            <div className="auth-input-group">
              <label htmlFor="reset-confirm-password">Confirm Password</label>
              <input
                id="reset-confirm-password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            {error && <div className="auth-error" role="alert">{error}</div>}
            {success && <div className="auth-success" role="status">{success}</div>}

            <button type="submit" className="auth-btn-primary" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>

            <button
              type="button"
              className="auth-btn-secondary"
              onClick={handleResendCode}
            >
              Start Over
            </button>
          </form>
        )}

        {step === 1 && (
          <button
            type="button"
            className="auth-link-btn"
            onClick={() => setPage('login')}
          >
            Remember your password? <strong>Sign In</strong>
          </button>
        )}
      </div>
    </div>
  );
}
export { extractErrorMessage };